import { yupResolver } from '@hookform/resolvers/yup'
import { Typography } from '@/app/components/Typography'
import { useEffect, useMemo, useRef, useState } from 'react'
import * as yup from 'yup'

import { Controller, FormProvider, useForm } from 'react-hook-form'

import { ErrorMessage } from '@/app/components/ErrorMessage'
import {
  INotificationStatus,
  useNotificationContext,
} from '@/app/components/Notification'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { Headline2 } from '@/app/components/Typography'
import { useToolsSuggestions } from '@/app/hooks/data-hooks/useToolsSuggestions'
import { useUserProfile } from '@/app/hooks/data-hooks/useUserProfile'
import { useUserProfileMutation } from '@/app/hooks/data-hooks/useUserProfileMutation'
import Apps from '@mui/icons-material/Apps'
import WarningAmber from '@mui/icons-material/WarningAmber'
import { Checkbox } from '@/app/modules/shared/components/checkbox'
import { ProgressBar } from '@/app/modules/shared/components/progress-bar'
import { IToolSuggestion } from '@/types/tool-suggestion'
import { ToolSearchInput } from './ToolSearchInput'
import { useNamespacedIntl } from '../lib/useNamespacedIntl'

export type UserToolsStepValues = {
  selectedToolIds: string[]
  exploreNewTools: boolean
}

type UserToolsStepProps = {
  onContinue: () => void | Promise<void>
  maxSteps: number
  currentStep: number
}

const TOOL_CARD_SKELETON_COUNT = 12
const TOOL_SKELETON_NAME_BAR_WIDTHS_PX = [
  48, 132, 64, 120, 76, 100, 52, 144, 84, 96, 70, 112,
]

function faviconUrlForDomain(domain: string): string | null {
  const host = domain.replace(/^https?:\/\//i, '').split('/')[0]?.trim()
  if (!host) return null
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`
}

function ToolCardSkeletonGrid({ loadingLabel }: { loadingLabel: string }) {
  return (
    <div
      className="flex flex-wrap gap-2 md:gap-3"
      aria-busy="true"
      role="status"
      aria-label={loadingLabel}
    >
      {Array.from({ length: TOOL_CARD_SKELETON_COUNT }).map((_, i) => {
        const nameW =
          TOOL_SKELETON_NAME_BAR_WIDTHS_PX[
            i % TOOL_SKELETON_NAME_BAR_WIDTHS_PX.length
          ]
        return (
          <div
            key={i}
            className="flex w-max max-w-full min-w-0 shrink-0 items-center gap-2 rounded-xl border border-solid border-lines bg-[#F5F6F8] p-2"
          >
            <div className="h-8 w-8 shrink-0 animate-pulse rounded-md bg-[#D7D9DE]" />
            <div
              className="h-3.5 min-w-0 max-w-full shrink-0 animate-pulse rounded bg-[#D7D9DE]"
              style={{ width: nameW }}
            />
            <div className="h-5 w-5 shrink-0 animate-pulse rounded bg-[#D7D9DE]" />
          </div>
        )
      })}
    </div>
  )
}

function ToolSuggestionError({ errorMessage }: { errorMessage: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-error bg-error-light px-3 py-3 text-sm font-normal text-error">
      <WarningAmber className="h-5 w-5 shrink-0 text-error" aria-hidden />
      {errorMessage}
    </div>
  )
}

function ToolIcon({
  tool,
  brokenFavicon,
  onFaviconError,
}: {
  tool: IToolSuggestion
  brokenFavicon: boolean
  onFaviconError: () => void
}) {
  const url = tool.logo?.url || faviconUrlForDomain(tool.domain)
  if (!url || brokenFavicon) {
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-600">
        <Apps className="h-5 w-5" aria-hidden />
      </span>
    )
  }
  return (
    <img
      src={url}
      alt=""
      width={30}
      height={30}
      className="h-8 w-8 shrink-0 rounded-md object-contain"
      onError={onFaviconError}
    />
  )
}

export function UserToolsStep({
  onContinue,
  maxSteps,
  currentStep,
}: UserToolsStepProps) {
  const t = useNamespacedIntl('UserOnboarding')
  const tCommon = useNamespacedIntl('common')
  const { setMessage } = useNotificationContext()
  const { data: { data: userProfile } = {} } = useUserProfile()
  const { mutateAsync: updateProfile, isLoading: isMutating } =
    useUserProfileMutation(userProfile?._id as string)

  const profileId = userProfile?._id

  const {
    data: { data: suggestions = [] } = {},
    isLoading: isLoadingSuggestions,
    isError: isSuggestionsError,
  } = useToolsSuggestions(profileId)

  const [extraTools, setExtraTools] = useState<IToolSuggestion[]>([])
  const extraToolsInitializedRef = useRef(false)

  // Populate extra tools once: profile tools that are not in suggestions
  useEffect(() => {
    if (extraToolsInitializedRef.current || isLoadingSuggestions || !userProfile)
      return
    extraToolsInitializedRef.current = true
    const profileTools = userProfile.tools ?? []
    const extra = profileTools.filter(
      (tool) => !suggestions.some((s) => s._id === tool._id)
    )
    setExtraTools(extra)
  }, [isLoadingSuggestions, userProfile, suggestions])

  const displayTools = useMemo(
    () => [...suggestions, ...extraTools],
    [suggestions, extraTools]
  )

  const [brokenFavicons, setBrokenFavicons] = useState<Record<string, boolean>>(
    {}
  )

  const [showGeneratingMessage, setShowGeneratingMessage] = useState(false)

  useEffect(() => {
    if (!isLoadingSuggestions) {
      setShowGeneratingMessage(false)
      return
    }
    const timer = setTimeout(() => setShowGeneratingMessage(true), 3000)
    return () => clearTimeout(timer)
  }, [isLoadingSuggestions])

  const initialSelectedToolIds = useMemo(() => {
    const saved = userProfile?.tools
    if (!saved?.length) return []
    return saved.map((tool) => tool._id).filter(Boolean) as string[]
  }, [userProfile?.tools])

  const formSchema = useMemo(
    () =>
      yup.object<UserToolsStepValues>().shape({
        selectedToolIds: yup.array().of(yup.string().required()).default([]),
        exploreNewTools: yup.boolean().default(false),
      }),
    []
  )

  const methods = useForm<UserToolsStepValues>({
    resolver: yupResolver(formSchema),
    disabled: isMutating,
    values: {
      selectedToolIds: initialSelectedToolIds,
      exploreNewTools: userProfile?.exploreNewTools ?? false,
    },
  })

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = methods
  const formRef = useRef<HTMLFormElement>(null)
  const selectedIds = watch('selectedToolIds')

  const selectedIdsSet = useMemo(
    () => new Set(selectedIds ?? []),
    [selectedIds]
  )

  const toggleTool = (id: string) => {
    const current = methods.getValues('selectedToolIds') ?? []
    if (current.includes(id)) {
      methods.setValue(
        'selectedToolIds',
        current.filter((i) => i !== id)
      )
    } else {
      methods.setValue('selectedToolIds', [...current, id])
    }
  }

  const handleAddTool = (tool: IToolSuggestion) => {
    const isInDisplay = displayTools.some((t) => t._id === tool._id)
    if (!isInDisplay) {
      setExtraTools((prev) => [...prev, tool])
    }
    const current = methods.getValues('selectedToolIds') ?? []
    if (!current.includes(tool._id)) {
      methods.setValue('selectedToolIds', [...current, tool._id])
    }
  }

  const handleOnSubmit = async (data: UserToolsStepValues) => {
    const tools = Array.from(new Set(data.selectedToolIds.filter(Boolean)))
    try {
      await updateProfile({
        tools,
        exploreNewTools: data.exploreNewTools,
      })
      await onContinue()
    } catch {
      setMessage(t('profile_update_failed'), INotificationStatus.ERROR)
    }
  }

  const cardsDisabled = isMutating || !!(profileId && isLoadingSuggestions)

  return (
    <FormProvider {...methods}>
      <form
        className="flex px-6 pb-3 md:px-0 md:py-8"
        onSubmit={handleSubmit(handleOnSubmit)}
        ref={formRef}
      >
        <div className="flex w-full flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Headline2>{t('tools_heading')}</Headline2>
            <Typography variant="body2" className="text-primary-light">
              {t('tools_subtitle')}
            </Typography>
          </div>

          {errors.root?.serverError?.message && (
            <ErrorMessage
              message={errors.root?.serverError?.message}
              className="mt-2 text-left"
            />
          )}

          <div className="flex flex-col gap-2">
            <Typography
              variant="body2"
              className="font-semibold text-gray-light"
            >
              {t('tools_select_label')}
            </Typography>
            {isSuggestionsError ? (
              <ToolSuggestionError errorMessage={t('tools_suggestions_error')} />
            ) : isLoadingSuggestions ? (
              <>
                {showGeneratingMessage && (
                  <div className="inline-flex items-center gap-2 self-start rounded-full bg-mydra-gray px-2 py-1">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full bg-mydra-purple"
                      aria-hidden
                    />
                    <Typography
                      variant="body2"
                      className="font-medium text-mydra-purple"
                    >
                      {t('tools_generating_message')}
                    </Typography>
                  </div>
                )}
                <ToolCardSkeletonGrid loadingLabel={t('loading_options')} />
              </>
            ) : displayTools.length === 0 ? (
              <p className="text-sm text-gray-500">{t('tools_empty')}</p>
            ) : (
              <Controller
                control={control}
                name="selectedToolIds"
                render={() => (
                  <div className="flex flex-wrap gap-3">
                    {displayTools.map((tool) => {
                      const checked = selectedIdsSet.has(tool._id)
                      const faviconKey = tool._id
                      return (
                        <Checkbox
                          key={tool._id}
                          id={`tool-${tool._id}`}
                          name={`tool-${tool._id}`}
                          checked={checked}
                          disabled={cardsDisabled}
                          onChange={() => toggleTool(tool._id)}
                          variant="medium"
                          label={
                            <span className="flex min-w-0 items-center gap-2">
                              <ToolIcon
                                tool={tool}
                                brokenFavicon={!!brokenFavicons[faviconKey]}
                                onFaviconError={() =>
                                  setBrokenFavicons((prev) => ({
                                    ...prev,
                                    [faviconKey]: true,
                                  }))
                                }
                              />
                              <Typography
                                variant="body2"
                                as="span"
                                className="font-medium"
                              >
                                {tool.name}
                              </Typography>
                            </span>
                          }
                          labelProps={{
                            className: [
                              'flex w-max max-w-full min-w-0 shrink-0 cursor-pointer items-center gap-2 rounded-xl border border-solid bg-white p-2 flex-row-reverse !font-normal',
                              '[&>div:first-child]:!flex-none [&>div:first-child]:shrink-0 [&>span]:!w-auto [&>span]:min-w-0 [&>span]:flex-1',
                              checked
                                ? 'border-mydra-purple'
                                : 'border-lines hover:border-gray-300',
                              cardsDisabled
                                ? 'cursor-not-allowed opacity-60'
                                : '',
                            ]
                              .filter(Boolean)
                              .join(' '),
                          }}
                          glyphProps={{ className: 'text-gray-400' }}
                        />
                      )
                    })}
                  </div>
                )}
              />
            )}

            <ToolSearchInput
              onAddTool={handleAddTool}
              selectedIds={selectedIdsSet}
              disabled={cardsDisabled}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Typography variant="body2" className="font-medium">
              {t('tools_explore_new_label')}
            </Typography>
            <Controller
              control={control}
              name="exploreNewTools"
              render={({ field }) => (
                <div className="flex flex-wrap gap-3">
                  {(
                    [
                      { value: true, label: tCommon('yes') },
                      { value: false, label: tCommon('no') },
                    ] as const
                  ).map((opt) => {
                    const checked = field.value === opt.value
                    return (
                      <Checkbox
                        key={String(opt.value)}
                        id={`explore-new-tools-${opt.value}`}
                        checked={checked}
                        disabled={isMutating}
                        onChange={() => field.onChange(opt.value)}
                        variant="medium"
                        label={
                          <Typography
                            variant="body2"
                            as="span"
                            className="font-medium"
                          >
                            {opt.label}
                          </Typography>
                        }
                        labelProps={{
                          className: [
                            'flex w-max max-w-full min-w-0 shrink-0 cursor-pointer items-center gap-2 rounded-xl border border-solid bg-white p-2 flex-row-reverse !font-normal',
                            '[&>div:first-child]:!flex-none [&>div:first-child]:shrink-0',
                            checked
                              ? 'border-mydra-purple'
                              : 'border-lines hover:border-gray-300',
                            isMutating ? 'cursor-not-allowed opacity-60' : '',
                          ]
                            .filter(Boolean)
                            .join(' '),
                        }}
                        glyphProps={{ className: 'text-gray-400' }}
                      />
                    )
                  })}
                </div>
              )}
            />
          </div>

          <div className="mt-auto flex w-full md:justify-end">
            <ProgressBar
              max={maxSteps}
              progress={currentStep}
              className="mr-auto self-center md:w-32"
            />
            <PrimaryButton
              type="submit"
              disabled={isMutating || !!(profileId && isLoadingSuggestions)}
              isLoading={isSubmitting}
              className="w-full md:w-auto"
            >
              {t('continue')}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
